import React, {useState, useEffect, useRef} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import DatePicker from "react-datepicker";
import {
    formatDateTimeToString,
    formatDateToAmericanFormat,
    formatDateToString, UserPermission, statusTranslate, renderAutomotiveServiceOptions, CompanyPermission
} from "../../utils/utils";
import Config from "../../core/config";
import {updateAutomotiveServiceStatus} from "../../utils/requestUtils";
import {useRecoilState} from "recoil";import {getAuthHeader} from "../../utils/auth";
import {useNavigate} from "react-router-dom";
import 'react-datepicker/dist/react-datepicker.css';
import ModalDiv from "../../ModalDiv";
import {useReactToPrint} from "react-to-print";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import Loading from "../../core/Loading";
import {loadingAtom} from "../../atoms";
import {usePermission} from "../../core/PermissionContext";
import PaymentMethodValueSelect from "../paymentMehod/PaymentMethodValueSelect";
import AutomotiveServiceViewer from "./AutomotiveServiceViewer";
import eyeImg from "../../img/icons8-eye-24.png";

const apiUrl = Config.apiUrl;

const formatMoneyBr = (value) =>
    Number(value ?? 0).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2});

const statusStyle = (statusKey) => statusTranslate[statusKey] || statusTranslate.OPENING;

const AutomotiveServiceList = () => {
    const { hasUserPermission, loadingPermissions, hasCompanyPermission } = usePermission();
    const [services, setServices] = useState([]);
    const [startDate, setStartDate] = useState(undefined);
    const [endDate, setEndDate] = useState(undefined);
    const [status, setStatus] = useState('IN_PROGRESS');
    const [totalValue, setTotalValue] = useState(0);
    const navigate = useNavigate();
    const [serviceShowPrintDialogIndex, setServiceShowPrintDialogIndex] = useState(undefined);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Estados para controle do modal de pagamento
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [serviceToUpdate, setServiceToUpdate] = useState(null);
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    const [showServiceViewer, setShowServiceViewer] = useState(false);
    const [selectedServiceId, setSelectedServiceId] = useState(null);

    const componentRef = useRef();

    const [loading, setLoading] = useRecoilState(loadingAtom);

    async function makeQuery() {
        try {
            const newStartDate = startDate ? startDate : new Date(1900, 0, 1);
            const newEndDate = endDate ? endDate : new Date(2100, 0, 1);
            const url = `${apiUrl}/api/automotiveservices/queryByDateAndSearch?startDate=${formatDateToAmericanFormat(newStartDate)}&endDate=${formatDateToAmericanFormat(newEndDate)}&status=${encodeURIComponent(status)}&searchTerm=${encodeURIComponent(searchTerm ?? '')}`;
            setLoading(true);
            const response = await makeRequest(url, 'GET');
            if (!Array.isArray(response)) {
                setServices([]);
                return;
            }
            const updatedResponse = response.map((service) => ({
                ...service,
                customerName: (service.customerName ?? '').toString().toUpperCase(),
            }));
            setServices(updatedResponse);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        makeQuery();
    }, []);

    useEffect(() => {
        setTotalValue(services.reduce((acc, obj) => acc + obj.totalValue, 0));
    }, [services]);

    async function removeService(id) {
        try {
            setLoading(true);
            const url = apiUrl + '/api/automotiveservices/' + id;
            await makeRequest(url, 'DELETE');
            await makeQuery();
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleQuery = async (e) => {
        await makeQuery();
    };

    const handleStartDateChange = (date) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
    };

    const [sortConfig, setSortConfig] = useState({key: 'deliveryDate', direction: 'ascending'});

    const [sortedServices, setSortedServices] = useState([]);

    useEffect(() => {
        setSortedServices([...services].sort((a, b) => {
            const aValue = sortConfig.key.includes('.') ? a[sortConfig.key.split('.')[0]][sortConfig.key.split('.')[1]] : a[sortConfig.key];
            const bValue = sortConfig.key.includes('.') ? b[sortConfig.key.split('.')[0]][b[sortConfig.key.split('.')[1]]] : b[sortConfig.key];

            if (aValue < bValue) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        }));
    }, [services, sortConfig]);

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({key, direction});
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? '▲' : '▼';
        }
        return '';
    };

    function printService(serviceIndex) {
        setServiceShowPrintDialogIndex(serviceIndex);
    }

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: '',
        pageStyle: `
    @media print {
        * {
            font-size: 12px !important;
        }
    }
    `,
    });

    const handleInputChange = (e) => {
        const string = e.target.value;
        setSearchTerm(string);
    };

    async function handleStatus(index, status) {
        // Se a permissão está habilitada e o status é CONCLUDED, mostrar modal de pagamento
        if (hasCompanyPermission(CompanyPermission.REQUIRE_PAYMENT_CONFIRMATION_FOR_AUTOMOTIVE_SERVICE) && status === 'CONCLUDED') {
            setServiceToUpdate(sortedServices[index]);
            setPendingStatusChange(status);
            setShowPaymentModal(true);
            return;
        }
        
        // Comportamento normal para outros status
        const response = await updateAutomotiveServiceStatus(sortedServices[index].id, status);
        if (response !== undefined) {
            setSortedServices((prevItems) =>
                prevItems.map((item, i) => {
                        return i === index ? {
                            ...item,
                            status: status,
                        } : item;
                    }
                )
            );
        } else {
            alert('O status não foi atualizado em nenhum serviço.');
        }
    }

    // Função para lidar com a confirmação do pagamento
    const handlePaymentConfirm = async (paymentMethodValues) => {
        try {
            setLoading(true);
            
            // Preparar dados dos pagamentos
            const paymentData = paymentMethodValues.map(payment => ({
                paymentMethod: payment.paymentMethod,
                value: payment.value,
                dueDate: payment.dueDate,
                paid: payment.paid,
                splits: payment.splits || 0
            }));

            // Atualizar apenas os pagamentos usando o novo endpoint
            const url = `${apiUrl}/api/automotiveservices/${serviceToUpdate.id}/payments`;
            const response = await makeRequest(url, 'PUT', {
                'Content-Type': 'application/json',
            }, JSON.stringify(paymentData));

            if (response) {
                // Se há um status pendente, atualizar o status do serviço
                if (pendingStatusChange) {
                    const statusResponse = await updateAutomotiveServiceStatus(serviceToUpdate.id, pendingStatusChange);
                    if (statusResponse !== undefined) {
                        // Atualizar a lista local
                        setSortedServices((prevItems) =>
                            prevItems.map((item) => {
                                if (item.id === serviceToUpdate.id) {
                                    return {
                                        ...item,
                                        status: pendingStatusChange,
                                        paymentMethods: paymentData
                                    };
                                }
                                return item;
                            })
                        );
                    }
                } else {
                    // Se não há status pendente, apenas atualizar os pagamentos
                    setSortedServices((prevItems) =>
                        prevItems.map((item) => {
                            if (item.id === serviceToUpdate.id) {
                                return {
                                    ...item,
                                    paymentMethods: paymentData
                                };
                            }
                            return item;
                        })
                    );
                    // Recarregar a lista para garantir que os dados estão atualizados
                    await makeQuery();
                }
            }
        } catch (error) {
            console.error('Error updating payments:', error);
            alert('Erro ao atualizar pagamentos. Tente novamente.');
        } finally {
            setLoading(false);
            setShowPaymentModal(false);
            setServiceToUpdate(null);
            setPendingStatusChange(null);
        }
    };

    // Função para fechar o modal de pagamento
    const handlePaymentClose = () => {
        setShowPaymentModal(false);
        setServiceToUpdate(null);
        setPendingStatusChange(null);
    };

    const openServiceViewer = (serviceId) => {
        setSelectedServiceId(serviceId);
        setShowServiceViewer(true);
    };

    const closeServiceViewer = () => {
        setShowServiceViewer(false);
        setSelectedServiceId(null);
    };

    if (loadingPermissions) {
        return <Loading></Loading>;
    }
    if (!hasUserPermission(UserPermission.LIST_AUTOMOTIVE_SERVICE)) {
        navigate('/unauthorizedaccess');
        return null;
    }

    return (
        <>
            {serviceShowPrintDialogIndex >= 0 && sortedServices[serviceShowPrintDialogIndex] && (
                <ModalDiv closeModal={() => setServiceShowPrintDialogIndex(undefined)} style_={{width: '400px'}}>
                    <div ref={componentRef}>
                        <div style={{marginLeft: '5mm'}}>
                            {(() => {
                                const s = sortedServices[serviceShowPrintDialogIndex];
                                const saleDate = s.saleDate ? formatDateToString(new Date(s.saleDate)) : '—';
                                const deliveryDate = s.deliveryDate ? formatDateTimeToString(new Date(s.deliveryDate)) : '—';
                                return (
                                    <>
                            <div
                                style={{marginBottom: '5px'}}>CÓDIGO: {s.code}</div>
                            <div
                                style={{marginBottom: '5px'}}>CLIENTE: {s.customerName}</div>
                            <div
                                style={{marginBottom: '5px'}}>CONTATO: {s.customerPhone}</div>
                            <div
                                style={{marginBottom: '5px'}}>CARRO: {s.car}</div>
                            <div
                                style={{marginBottom: '5px'}}>MARCA: {s.carBrand}</div>
                            <div
                                style={{marginBottom: '5px'}}>PLACA: {s.licensePlate}</div>
                            <div
                                style={{marginBottom: '5px'}}>MILEAGE: {s.mileage}</div>
                            <div
                                style={{marginBottom: '5px'}}>YEAR: {s.modelYear}</div>
                            <div
                                style={{marginBottom: '5px'}}>SERVIÇO: {saleDate}</div>
                            <div
                                style={{marginBottom: '5px'}}>ENTREGA: {deliveryDate}</div>
                            <div style={{marginBottom: '5px'}}>VALOR:
                                R$ {formatMoneyBr(s.totalValue)}</div>
                                    </>
                                );
                            })()}
                            
                            {/* Items */}
                            {sortedServices[serviceShowPrintDialogIndex].items && sortedServices[serviceShowPrintDialogIndex].items.length > 0 && (
                                <>
                                    <div style={{marginBottom: '10px', marginTop: '10px', fontWeight: 'bold'}}>ITENS:</div>
                                    {sortedServices[serviceShowPrintDialogIndex].items.map((item, index) => (
                                        <div key={index} style={{marginBottom: '5px', marginLeft: '10px'}}>
                                            <div>• {item.quantity}x [{item.type}] {item.product ? item.product.name : 'Produto não encontrado'} - R$ {formatMoneyBr(item.value)}</div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                </ModalDiv>
            )}

            {/* Modal de confirmação de pagamento */}
            {showPaymentModal && serviceToUpdate && (
                <PaymentMethodValueSelect
                    onClose={handlePaymentClose}
                    onConfirm={handlePaymentConfirm}
                    totalValue={serviceToUpdate.totalValue}
                    paymentMethods={serviceToUpdate.paymentMethods || []}
                    futureDueDate={serviceToUpdate.deliveryDate ? new Date(serviceToUpdate.deliveryDate) : new Date()}
                    allowChangeValue={true}
                    allowChangeDueDate={true}
                />
            )}

            <div style={{display: "flex", alignItems: "center", gap: '10px'}}>
                <div className="header-label">Consulta de Serviços Automotivos</div>
                {hasUserPermission(UserPermission.CREATE_AUTOMOTIVE_SERVICE) &&
                    <button className={"new-button"} onClick={() => navigate('/app/automotiveservice')}>Novo</button>
                }
            </div>
            <div
                style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%'}}>

                <div style={{marginTop: '5px', alignItems: 'center', display: "flex"}}>
                    <div style={{alignItems: 'center'}}>
                        Código/Cliente/Carro
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <input type="text" value={searchTerm} onChange={handleInputChange} data-test="search-input"/>
                        </div>
                    </div>
                    <div style={{flexDirection: "column", alignItems: 'center'}}>

                        Data de Entrega:
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <DatePicker
                                className="datepicker"
                                selected={startDate}
                                onChange={handleStartDateChange}
                                dateFormat="dd/MM/yyyy"
                                isClearable
                                autoComplete="off"
                            />

                            até
                            <DatePicker
                                className="datepicker"
                                selected={endDate}
                                onChange={handleEndDateChange}
                                dateFormat="dd/MM/yyyy"
                                isClearable
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div style={{flexDirection: "column", alignItems: 'center'}}>

                        Status
                        <div style={{display: 'flex', alignItems: 'center'}}>

                            <select value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option key="ALL" value="ALL">
                                    {statusTranslate.ALL.label}
                                </option>
                                <option key="IN_PROGRESS" value="IN_PROGRESS">
                                    Em Andamento
                                </option>
                                {renderAutomotiveServiceOptions(false)}
                            </select>

                            <button onClick={handleQuery}>Consultar</button>
                        </div>
                    </div>

                </div>

            </div>

            <table style={{height: '100%', overflowY: 'unset !important', overflowX: 'unset !important'}}>

                <tr>
                    <th style={{textAlign: 'center', cursor: "pointer", minWidth: "70px"}}
                        onClick={() => handleSort('code')}>
                        {getSortIcon('code')} Código
                    </th>
                    <th style={{cursor: "pointer", minWidth: "100px"}} onClick={() => handleSort('customerName')}>
                        {getSortIcon('customerName')} Cliente
                    </th>
                    <th style={{cursor: "pointer", minWidth: "100px"}} onClick={() => handleSort('car')}>
                        {getSortIcon('car')} Carro
                    </th>
                    <th style={{textAlign: 'center', cursor: "pointer", minWidth: "100px"}}
                        onClick={() => handleSort('deliveryDate')}>
                        {getSortIcon('deliveryDate')} Data Entrega
                    </th>
                    <th style={{cursor: "pointer", minWidth: "70px", textAlign: 'right'}}
                        onClick={() => handleSort('totalValue')}>
                        {getSortIcon('totalValue')} Valor
                    </th>
                    <th style={{textAlign: 'center', cursor: "pointer", minWidth: "100px"}}
                        onClick={() => handleSort('status')}>
                        {getSortIcon('status')} Status
                    </th>
                    <th/>
                </tr>
                <tbody style={{overflowY: 'auto'}}>
                {sortedServices.map((service, index) => {
                    const st = statusStyle(service.status);
                    return (
                    <tr key={service.id}>
                        <td>
                            <div style={{textAlign: 'center'}}>{service.code}</div>
                        </td>
                        <td>
                            <div>{service.customerName}</div>
                        </td>
                        <td>
                            <div>{service.car}</div>
                        </td>
                        <td>
                            <div style={{textAlign: 'center'}}>
                                {service.deliveryDate ? formatDateTimeToString(new Date(service.deliveryDate)) : '—'}
                            </div>
                        </td>
                        <td>
                            <div style={{textAlign: 'right'}}>{formatMoneyBr(service.totalValue)}</div>
                        </td>
                        <td>
                            <div style={{
                                padding: '3px',
                                backgroundColor: st.color,
                                borderRadius: '3px',
                                borderColor: st.color,
                                justifyContent: 'center'
                            }}>
                                <select
                                    style={{
                                        padding: '2px',
                                        backgroundColor: st.color,
                                        borderColor: st.color
                                    }}
                                    value={service.status} onChange={(e) => {
                                        // eslint-disable-next-line no-restricted-globals
                                        if (!confirm(`Deseja realmente mudar o status do serviço ${service.code} de '${st.label}' para '${statusStyle(e.target.value).label}'?`)) {
                                            return;
                                        }
                                        handleStatus(index, e.target.value)
                                    }}>
                                    {renderAutomotiveServiceOptions(false)}
                                </select>
                            </div>
                        </td>
                        <td>
                            <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
                                <img
                                    src={eyeImg}
                                    alt="Visualizar"
                                    title="Visualizar detalhes"
                                    style={{cursor: "pointer", width: "20px", height: "20px", marginRight: '5px'}}
                                    onClick={() => openServiceViewer(service.id)}
                                />
                                {hasUserPermission(UserPermission.EDIT_AUTOMOTIVE_SERVICE) &&
                                    <button className="edit-button" onClick={async () => {
                                        await navigate('/app/automotiveservice?id=' + service.id);
                                    }}>Editar
                                    </button>
                                }

                                <button
                                    onClick={() => {
                                        setServiceToUpdate(service);
                                        setShowPaymentModal(true);
                                    }}
                                    style={{marginLeft: '5px'}}
                                >
                                    Pagamentos
                                </button>

                                <button onClick={async () => {
                                    const url = `${apiUrl}/api/automotiveservices/${service.id}/report`;
                                    try {
                                        const response = await fetch(url, {
                                            method: 'GET',
                                            headers: {
                                                ...getAuthHeader(),
                                            }
                                        });

                                        if (response.ok) {
                                            const blob = await response.blob();
                                            const blobUrl = window.URL.createObjectURL(blob);

                                            // Abre o PDF em uma nova aba
                                            const newWindow = window.open(blobUrl, '_blank');

                                            // Limpa o blob URL após um tempo para liberar memória
                                            setTimeout(() => {
                                                window.URL.revokeObjectURL(blobUrl);
                                            }, 1000);
                                        } else {
                                            alert('Erro ao gerar relatório');
                                        }
                                    } catch (error) {
                                        console.error('Erro ao gerar relatório:', error);
                                        alert('Erro ao gerar relatório');
                                    }
                                }}>
                                    Imprimir
                                </button>

                                {hasUserPermission(UserPermission.REMOVE_AUTOMOTIVE_SERVICE) &&
                                    <button className="remove-button" onClick={async () => {
                                        // eslint-disable-next-line no-restricted-globals
                                        if (window.confirm('Deseja realmente excluir?')) {
                                            await removeService(service.id);
                                        }
                                    }}>Excluir
                                    </button>
                                }
                            </div>
                        </td>
                    </tr>
                    );
                })}
                </tbody>
                {services.length === 0 && (<tfoot>{loading ? 'Carregando...' : `Nenhum item encontrado.`}</tfoot>)}
            </table>
            {hasUserPermission(UserPermission.SHOW_TOTAL_AUTOMOTIVE_SERVICE_LIST) &&
                <div style={{marginTop: '5px', alignItems: 'center', display:'flex', gap:'10px'}}>
                    <div className="total-label">Quantidade: {services.length}</div>
                    <div className="total-label">Valor Total: R$ {formatMoneyBr(totalValue)}</div>
                </div>
            }
            {showServiceViewer && selectedServiceId && (
                <ModalDiv closeModal={closeServiceViewer}>
                    <AutomotiveServiceViewer
                        serviceId={selectedServiceId}
                        onClose={closeServiceViewer}
                    />
                </ModalDiv>
            )}
        </>
    );
};

export default AutomotiveServiceList; 